import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Activity, Play, History, ChevronDown, Loader2, LineChart, Terminal, Download, Bell, BellRing } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import TickerRibbon from "@/components/dashboard/TickerRibbon";
import Pipeline from "@/components/dashboard/Pipeline";
import UniversePanel from "@/components/dashboard/UniversePanel";
import SignalBoard from "@/components/dashboard/SignalBoard";
import DeepDive from "@/components/dashboard/DeepDive";
import ScanHistory from "@/components/dashboard/ScanHistory";
import SectorHeatmap from "@/components/dashboard/SectorHeatmap";
import CompareTray from "@/components/dashboard/CompareTray";
import MobileWorkspace from "@/components/dashboard/MobileWorkspace";
import OfflineOverlay from "@/components/pwa/OfflineOverlay";
import { usePwa } from "@/lib/usePwa";
import { useIsMobile } from "@/lib/useIsMobile";
import { getStatus, getMarkets, getAnalyses, runScan } from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { toast } from "sonner";

const SCOPES = [
  { key: "quick", label: "QUICK (core symbols + ETFs · ~1 min)" },
  { key: "nifty", label: "NIFTY 50 (India)" },
  { key: "sp500", label: "S&P 500 (US)" },
  { key: "etf", label: "ETFs & INDICES (SPY, QQQ, NiftyBees, Nasdaq…)" },
  { key: "full", label: "FULL UNIVERSE (Stocks + ETFs + Indices)" },
];

const NavBtn = ({ children, ...p }) => (
  <button {...p} className="flex items-center gap-1.5 text-[12px] font-semibold tracking-wide px-3 py-2 rounded-[3px] border border-white/15 text-slate-200 hover:bg-white/10 hover:text-white transition-colors duration-150">
    {children}
  </button>
);

export default function Dashboard() {
  const [status, setStatus] = useState(null);
  const [indices, setIndices] = useState([]);
  const [items, setItems] = useState([]);
  const [universe, setUniverse] = useState("all");
  const [signalFilter, setSignalFilter] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [compare, setCompare] = useState([]);
  const [loading, setLoading] = useState(true);

  const addCompare = (t) => setCompare((prev) => {
    if (prev.includes(t)) { toast.info(`${t.replace(".NS", "").replace("^", "")} already in compare`); return prev; }
    if (prev.length >= 3) { toast.warning("Compare holds up to 3 tickers"); return prev; }
    toast.success(`Added ${t.replace(".NS", "").replace("^", "")} to compare`);
    return [...prev, t];
  });
  const removeCompare = (t) => setCompare((prev) => prev.filter((x) => x !== t));
  const wasRunning = useRef(false);
  const consoleRef = useRef(null);
  const isMobile = useIsMobile();
  const { isOffline, canInstall, promptInstall, pushSupported, pushEnabled, pushBusy, enablePush, disablePush } = usePwa();

  const loadAnalyses = useCallback(() => {
    getAnalyses({ universe, signal: signalFilter, q })
      .then((d) => setItems(d.items || []))
      .finally(() => setLoading(false));
  }, [universe, signalFilter, q]);

  useEffect(() => { loadAnalyses(); }, [loadAnalyses]);

  const refreshAll = useCallback(async () => {
    await Promise.all([
      getAnalyses({ universe, signal: signalFilter, q }).then((d) => setItems(d.items || [])).catch(() => {}),
      getStatus().then(setStatus).catch(() => {}),
      getMarkets().then((d) => setIndices(d.indices || [])).catch(() => {}),
    ]);
    toast.success("Data refreshed");
  }, [universe, signalFilter, q]);

  // App-icon badge (installed PWA) reflecting matched setups
  useEffect(() => {
    const n = status?.matched_count ?? 0;
    if (typeof navigator === "undefined" || !("setAppBadge" in navigator)) return;
    if (n > 0) navigator.setAppBadge(n).catch(() => {});
    else navigator.clearAppBadge?.().catch(() => {});
  }, [status?.matched_count]);

  useEffect(() => {
    getMarkets().then((d) => setIndices(d.indices || [])).catch(() => {});
    const mi = setInterval(() => getMarkets().then((d) => setIndices(d.indices || [])).catch(() => {}), 60000);
    return () => clearInterval(mi);
  }, []);

  useEffect(() => {
    let timer;
    const poll = () => getStatus().then((s) => {
      setStatus(s);
      if (wasRunning.current && !s.scan.running) {
        toast.success(`Scan complete — ${s.scan.matches} setup(s) matched`);
        loadAnalyses();
      }
      wasRunning.current = s.scan.running;
      timer = setTimeout(poll, s.scan.running ? 3000 : 20000);
    }).catch(() => { timer = setTimeout(poll, 20000); });
    poll();
    return () => clearTimeout(timer);
  }, [loadAnalyses]);

  useEffect(() => {
    if (consoleRef.current) consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
  }, [status?.scan?.log]);

  const onRun = async (scope) => {
    try {
      const r = await runScan(scope);
      if (r.started) { toast.info(`Agentic scan started — ${scope.toUpperCase()}`); wasRunning.current = true; }
      else toast.warning(r.message || "Scan already running");
      getStatus().then(setStatus);
    } catch { toast.error("Failed to start scan"); }
  };

  const scan = status?.scan || {};
  const running = scan.running;

  return (
    <div className={`${isMobile ? "h-[100dvh] overflow-hidden" : "min-h-screen"} grid-bg flex flex-col`} data-testid="dashboard">
      {isOffline && <OfflineOverlay onRetry={() => window.location.reload()} />}
      {/* Top Nav */}
      <header className="sticky top-0 z-40 bg-[#121D28] border-b border-[#1E2D3E] text-white shadow-sm">
        <div className="flex items-center justify-between px-3 sm:px-4 h-14 gap-2 sm:gap-4">
          <div className="flex items-center gap-3 shrink-0">
            <div className="w-8 h-8 rounded-[4px] bg-[#0070BA] flex items-center justify-center">
              <LineChart size={16} className="text-white" />
            </div>
            <div className="leading-none">
              <div className="font-display text-[17px] font-extrabold tracking-tight text-white">ALPHA<span className="text-[#38BDF8]">SYNC</span></div>
              <div className="hidden sm:block text-[9px] tracking-[0.2em] text-slate-400 mt-0.5">SYSTEMATIC EQUITIES SCREENER</div>
            </div>
          </div>

          <div className="hidden lg:block flex-1 min-w-0 bg-[#0B131C] border-x border-[#1A2634] rounded-[4px] mx-2">
            <TickerRibbon indices={indices} />
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {pushSupported && (
              <button
                onClick={pushEnabled ? disablePush : enablePush}
                disabled={pushBusy}
                data-testid="push-toggle-btn"
                className="flex items-center gap-1.5 text-[12px] font-semibold tracking-wide px-3 py-2 rounded-[3px] border transition-colors duration-150 disabled:opacity-60"
                style={{
                  background: pushEnabled ? "rgba(0,229,153,0.15)" : "transparent",
                  borderColor: pushEnabled ? "rgba(52,211,153,0.5)" : "rgba(255,255,255,0.15)",
                  color: pushEnabled ? "#6EE7B7" : "#E2E8F0",
                }}
              >
                {pushBusy ? <Loader2 size={14} className="animate-spin" /> : pushEnabled ? <BellRing size={14} /> : <Bell size={14} />}
                <span className="hidden sm:inline">{pushEnabled ? "ALERTS ON" : "ALERTS"}</span>
              </button>
            )}
            {canInstall && (
              <button onClick={promptInstall} data-testid="install-btn"
                className="flex items-center gap-1.5 text-[12px] font-semibold tracking-wide px-3 py-2 rounded-[3px] bg-[#38BDF8]/15 border border-[#38BDF8]/40 text-[#7DD3FC] hover:bg-[#38BDF8]/25 transition-colors duration-150">
                <Download size={14} /><span className="hidden sm:inline">INSTALL APP</span>
              </button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button disabled={running} data-testid="run-scan-btn"
                  className="flex items-center gap-1.5 text-[12px] font-semibold tracking-wide px-2.5 sm:px-3.5 py-2 rounded-[3px] bg-[#0070BA] hover:bg-[#005B98] text-white transition-colors duration-150 disabled:opacity-60 shadow-xs">
                  {running ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
                  <span className="hidden sm:inline">{running ? "SCANNING" : "RUN SCAN"}</span>
                  {!running && <ChevronDown size={12} />}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white border-[#E2E8F0] text-slate-700 shadow-lg">
                {SCOPES.map((s) => (
                  <DropdownMenuItem key={s.key} onClick={() => onRun(s.key)} data-testid={`scan-scope-${s.key}`}
                    className="text-[13px] focus:bg-[#EFF6FF] focus:text-slate-900 cursor-pointer">
                    {s.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <ScanHistory trigger={<NavBtn data-testid="open-history-btn"><History size={13} /><span className="hidden sm:inline">HISTORY</span></NavBtn>} />
          </div>
        </div>
      </header>

      {isMobile && (
        <MobileWorkspace
          status={status} indices={indices} items={items}
          universe={universe} setUniverse={setUniverse}
          signalFilter={signalFilter} setSignalFilter={setSignalFilter}
          q={q} setQ={setQ} selected={selected} setSelected={setSelected}
          compare={compare} addCompare={addCompare} removeCompare={removeCompare}
          loading={loading} running={running} scan={scan} onRefresh={refreshAll}
        />
      )}

      {!isMobile && (
      <main className="flex-1 max-w-[1680px] w-full mx-auto p-3 sm:p-4 space-y-3.5">
        {/* Status strip */}
        <div className="bg-white border border-[#E2E8F0] rounded-[4px] shadow-xs px-4 py-2.5 flex flex-wrap items-center gap-x-6 gap-y-1.5 text-[13px] text-slate-500">
          <span className="flex items-center gap-1.5 font-semibold text-slate-700">
            <span className={`w-2 h-2 rounded-full ${running ? "bg-[#0284C7] animate-pulse" : "bg-[#00873C]"}`} />
            ENGINE {running ? "ACTIVE" : "ONLINE"}
          </span>
          <span>UNIVERSE <span className="text-slate-800 font-semibold tabular-nums">{status?.universe_counts?.total ?? "—"}</span> SYMBOLS</span>
          <span>ANALYSED <span className="text-slate-800 font-semibold tabular-nums">{status?.analysed_count ?? "—"}</span></span>
          <span>MATCHED <span className="font-semibold tabular-nums" style={{ color: "#00873C" }}>{status?.matched_count ?? 0}</span></span>
          <span>WATCH <span className="font-semibold tabular-nums" style={{ color: "#D97706" }}>{status?.monitor_count ?? 0}</span></span>
          <span>LAST SCAN <span className="text-slate-800 font-semibold">{status?.last_scan ? timeAgo(status.last_scan.finished_at) : "—"}</span></span>
          <span className="hidden sm:inline">HOURLY CRON <span className="font-semibold" style={{ color: status?.hourly_enabled ? "#00873C" : "#94A3B8" }}>{status?.hourly_enabled ? "ARMED" : "OFF"}</span></span>
        </div>

        <Pipeline stages={status?.stages || []} running={running} processed={scan.processed} total={scan.total} matches={scan.matches} current={scan.current} />

        <SectorHeatmap
          title="US Sector Heatmap"
          testid="sector-heatmap"
          universe="etf"
          onSelect={setSelected}
          refreshKey={status?.last_scan?.finished_at}
          sectors={[
            { t: "XLK", name: "Technology" }, { t: "XLF", name: "Financials" }, { t: "XLE", name: "Energy" },
            { t: "XLV", name: "Health" }, { t: "XLY", name: "Cons Disc" }, { t: "XLP", name: "Staples" },
            { t: "XLI", name: "Industrial" }, { t: "XLB", name: "Materials" }, { t: "XLU", name: "Utilities" },
            { t: "XLRE", name: "Real Est" }, { t: "XLC", name: "Comm Svc" },
          ]}
        />

        <SectorHeatmap
          title="India Sector Heatmap (NSE)"
          testid="sector-heatmap-in"
          universe="etf"
          onSelect={setSelected}
          refreshKey={status?.last_scan?.finished_at}
          sectors={[
            { t: "^NSEBANK", name: "Bank" }, { t: "^CNXIT", name: "IT" }, { t: "^CNXAUTO", name: "Auto" },
            { t: "^CNXPHARMA", name: "Pharma" }, { t: "^CNXFMCG", name: "FMCG" }, { t: "^CNXMETAL", name: "Metal" },
            { t: "^CNXREALTY", name: "Realty" }, { t: "^CNXENERGY", name: "Energy" },
          ]}
        />

        {/* Workspace */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3.5">
          <div className="lg:col-span-3 h-[440px] lg:h-[620px]">
            <UniversePanel items={items} universe={universe} setUniverse={setUniverse} q={q} setQ={setQ} selected={selected} onSelect={setSelected} loading={loading} />
          </div>
          <div className="lg:col-span-6 h-[560px] lg:h-[620px]">
            <SignalBoard items={items} signalFilter={signalFilter} setSignalFilter={setSignalFilter} selected={selected} onSelect={setSelected} matchedCount={status?.matched_count ?? 0} />
          </div>
          <div className="lg:col-span-3 h-[580px] lg:h-[620px]">
            <DeepDive ticker={selected} onCompare={addCompare} />
          </div>
        </div>

        {compare.length > 0 && (
          <CompareTray tickers={compare} onRemove={removeCompare} onSelect={setSelected} />
        )}

        {/* Live console */}
        {(running || (scan.log && scan.log.length > 0)) && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="border border-[#1E2D3E] bg-[#0F172A] rounded-[4px] overflow-hidden shadow-xs" data-testid="live-console">
            <div className="flex items-center gap-2 px-3 py-1.5 border-b border-[#1E2D3E]">
              <Terminal size={13} className="text-[#38BDF8]" />
              <span className="font-mono text-[12px] tracking-[0.16em] text-slate-400">LIVE CONSOLE</span>
              {running && <span className="font-mono text-[12px] text-[#38BDF8] ml-auto">{scan.processed}/{scan.total}</span>}
            </div>
            <div ref={consoleRef} className="max-h-36 overflow-y-auto p-2.5 space-y-0.5">
              {(scan.log || []).map((l, i) => (
                <div key={`${l.t}-${i}`} className="font-mono text-[12px] leading-snug">
                  <span className="text-slate-600">{new Date(l.t).toLocaleTimeString()}</span> <span className={l.msg.includes("MATCH") ? "text-[#34D399]" : "text-slate-300"}>{l.msg}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        <footer className="flex items-center gap-2 px-1 py-2 text-[12px] text-slate-400">
          <Activity size={13} /> Systematic screen for educational use · Not investment advice
        </footer>
      </main>
      )}
    </div>
  );
}
